import { useMemo, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { DatasetStatusEnum, EDatasetStatus, SecondaryStatusItem } from "types";
import { getCreationStatus } from "redux/CreateDataset/selectors";
import VLToastComponent from "views/components/VLToastComponent";
import Loader from "./Loader";
import Tile from "./Tile";

import styles from "./style.module.scss";
import { useFeatureFlagService } from "../../../../../../hooks/useFeatureFlagService";
import { NEW_STATUS } from "../../../../../../helpers/constants/filters";
export interface TileTypes {
  title: string;
  progress: number;
}

// Helper function to determine which subtasks should be visible for smooth UX
export const getVisibleSubtasks = (secondaryStatus: SecondaryStatusItem[]) => {
  if (!secondaryStatus || secondaryStatus.length === 0) {
    return [];
  }

  // Find the furthest progressed task
  let maxProgressedIndex = -1;
  for (let i = 0; i < secondaryStatus.length; i++) {
    if (secondaryStatus[i].progress > 0) {
      maxProgressedIndex = i;
    }
  }

  // If no task has started, show the first one
  if (maxProgressedIndex === -1) {
    return [secondaryStatus[0]];
  }

  // Show all tasks up to the furthest progressed one
  let tasksToShow = secondaryStatus.slice(0, maxProgressedIndex + 1);

  // If the current furthest task is completed (100%), also show the next one
  if (
    maxProgressedIndex < secondaryStatus.length - 1 &&
    secondaryStatus[maxProgressedIndex].progress === 100
  ) {
    tasksToShow.push(secondaryStatus[maxProgressedIndex + 1]);
  }

  return tasksToShow;
};

export default function CreateDataset() {
  const creationStatus = useSelector(getCreationStatus);
  const fatalErrorToastRef = useRef<any>(null);
  const navigate = useNavigate();
  const displayNewStatus = useFeatureFlagService(NEW_STATUS).show;

  // TODO: change to new status

  // Redirect to datasets list when user has no access
  useEffect(() => {
    if (displayNewStatus) {
      if (
        creationStatus?.status_new === EDatasetStatus.ERROR &&
        creationStatus?.fatalError === "ACCESS_DENIED"
      ) {
        navigate("/datasets");
      }
    } else {
      if (
        creationStatus?.status === DatasetStatusEnum.FATAL_ERROR &&
        creationStatus?.fatalError === "ACCESS_DENIED"
      ) {
        navigate("/datasets");
      }
    }
  }, [
    creationStatus?.status,
    creationStatus?.fatalError,
    navigate,
    displayNewStatus,
    creationStatus?.status_new,
  ]);

  // Transform secondary status to tiles with proper titles
  const tiles = useMemo<TileTypes[]>(() => {
    if (!creationStatus?.secondaryStatus) {
      return [];
    }

    // Map secondary status to tiles
    const statusToTitle: Record<string, string> = {
      UPLOADING: "Uploading",
      INITIALIZING: "Initializing",
      PRE_PROCESSING: "Pre Processing",
      INDEXING: "Indexing",
      SAVING: "Saving",
    };

    const visibleSubtasks = getVisibleSubtasks(creationStatus.secondaryStatus);

    return visibleSubtasks.map((item) => ({
      title: statusToTitle[item.status] || item.status,
      progress: item.progress,
    }));
  }, [creationStatus]);

  // Calculate overall progress
  const progress = useMemo(() => {
    if (!creationStatus?.secondaryStatus.length) return 0;
    const total = creationStatus.secondaryStatus.reduce(
      (sum, item) => sum + item.progress,
      0,
    );
    return Math.round(total / creationStatus.secondaryStatus.length);
  }, [creationStatus]);

  useEffect(() => {
    const isFatalError = displayNewStatus
      ? creationStatus?.status_new === EDatasetStatus.ERROR
      : creationStatus?.status === DatasetStatusEnum.FATAL_ERROR;

    if (
      isFatalError &&
      creationStatus?.fatalError &&
      !fatalErrorToastRef.current
    ) {
      fatalErrorToastRef.current = toast(
        <VLToastComponent
          title="Dataset creation failed"
          status="ERROR"
          description={creationStatus.fatalError}
          onClose={() => {
            toast.dismiss(fatalErrorToastRef.current);
            fatalErrorToastRef.current = null;
          }}
        />,
        { autoClose: false, closeButton: false, closeOnClick: false },
      );
    }
  }, [
    creationStatus?.status,
    creationStatus?.fatalError,
    displayNewStatus,
    creationStatus?.status_new,
  ]);

  return (
    <div className={styles.createDatasetSection}>
      <Loader progress={progress} />

      <div className={styles.tilesContainer}>
        {tiles.map((tile, index) => (
          <Tile
            key={tile.title}
            title={tile.title}
            progress={tile.progress}
            tileIndex={index}
          />
        ))}
      </div>
    </div>
  );
}
